import { useMemo, useState } from "react";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  DeleteOutlined,
  FileOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  Button,
  DatePicker,
  Form,
  Input,
  notification,
  Radio,
  Typography,
  Upload,
} from "antd";
import dayjs from "dayjs";
import {
  type AmendmentResponsibleSnapshot,
  type AmendmentDocumentType,
  type AmendmentRequest,
  useAmendmentsStore,
  validateAmendmentFile,
} from "@/features/journal-version-list";
import { PdfIcon } from "@/shared/assets/svg/icons";
import { UserBubbleLinkText } from "@/shared/ui";
import { formatAdministrativeDocument } from "@/shared/utils";
import {
  DocTypeEnum,
  DocTypeTypes,
} from "../../../journals-section-third/ui/journal-section-form/model/types/document-types";
import type { DocumentItem } from "../../../journals-section-third/ui/journal-section-form/ui/section-document-list/ui/section-document-modal/section-document-modal";
import { SectionDocumentModal } from "../../../journals-section-third/ui/journal-section-form/ui/section-document-list/ui/section-document-modal/section-document-modal";
import { useGetRepresentative } from "../../api";
import { AmendmentResponsiblePicker } from "./ui/amendment-responsible-picker";
import "./title-change-reason-form.scss";

const { Title, Text } = Typography;

interface TitleChangeReasonFormProps {
  isReadOnly?: boolean;
  request?: AmendmentRequest | null;
}

const DOCUMENT_TYPE_TO_MODAL_TYPE: Record<AmendmentDocumentType, DocTypeEnum> =
  {
    PERMIT: DocTypeTypes.PROJECT_DOC,
    EXECUTIVE: DocTypeTypes.WORK_DOC,
    SUPPORTING: DocTypeTypes.REGULATION_DOC,
  };

const FALLBACK_RESPONSIBLE_OPTIONS: AmendmentResponsibleSnapshot[] = [
  {
    id: "fallback-responsible-1",
    name: "Застроев Г. А.",
    organization: "ООО “Застройщик”",
    position: "Начальник участка",
    document: "Приказ № 12-ГГ от 12.10.2021",
    email: "izastroyschikov@zastroyschik.ru",
    registryNumber: "С-47-000001",
    nostroyNumber: "—",
    phone: "8 (921) 123-3210",
  },
  {
    id: "fallback-responsible-2",
    name: "Иванов И. И.",
    organization: "ООО “Застройщик”",
    position: "Инженер ПТО",
    document: "Приказ № 18 от 15.01.2025",
    email: "ivanov@example.ru",
    registryNumber: "—",
    nostroyNumber: "С-77-001234",
    phone: "8 (921) 000-00-00",
  },
];

const formatDate = (date: string | null) => {
  if (!date) return "—";

  return `${new Intl.DateTimeFormat("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Moscow",
  }).format(new Date(date))} МСК`;
};

interface FileLinkProps {
  name: string;
  onDelete?: () => void;
}

const FileLink = ({ name, onDelete }: FileLinkProps) => (
  <div className="title-change-reason-file-link">
    <div className="title-change-reason-file-link__content">
      <FileOutlined className="title-change-reason-file-link__icon" />
      <Text className="title-change-reason-form__link">{name}</Text>
    </div>
    {onDelete ? (
      <Button
        type="text"
        danger
        icon={<DeleteOutlined />}
        aria-label={`Удалить файл ${name}`}
        onClick={onDelete}
      />
    ) : null}
  </div>
);

const ResponsibleDocumentIcon = ({ documentUrl }: { documentUrl?: string }) => {
  if (!documentUrl) {
    return <PdfIcon />;
  }

  return (
    <a
      href={documentUrl}
      target="_blank"
      rel="noreferrer"
      aria-label="Открыть документ представителя"
    >
      <PdfIcon />
    </a>
  );
};

const ResponsibleCard = ({ request }: { request: AmendmentRequest }) => {
  const snapshot =
    request.responsibleSnapshot ??
    FALLBACK_RESPONSIBLE_OPTIONS.find(
      (option) => option.name === request.responsible,
    );

  return (
    <div className="title-change-reason-form__responsible">
      <UserBubbleLinkText>
        {snapshot?.name || request.responsible || "—"}
      </UserBubbleLinkText>
      <div className="title-change-reason-form__responsible-grid">
        <Text type="secondary">Организация</Text>
        <Text>{snapshot?.organization || "—"}</Text>
        <Text type="secondary">Должность</Text>
        <Text>{snapshot?.position || request.responsiblePosition || "—"}</Text>
        <Text type="secondary">Документ, №, дата</Text>
        <div className="title-change-reason-form__document-row">
          <Text>{snapshot?.document || "—"}</Text>
          {snapshot?.document ? (
            <ResponsibleDocumentIcon documentUrl={snapshot.documentUrl} />
          ) : null}
        </div>
        <Text type="secondary">Почта</Text>
        <Text className="title-change-reason-form__link">
          {snapshot?.email || "—"}
        </Text>
        <Text type="secondary">Номер в НРС</Text>
        <Text>{snapshot?.registryNumber || "—"}</Text>
        <Text type="secondary">Номер в НОСТРОЙ</Text>
        <Text>{snapshot?.nostroyNumber || "—"}</Text>
        <Text type="secondary">Телефон</Text>
        <Text>{snapshot?.phone || "—"}</Text>
      </div>
    </div>
  );
};

const RequestStatusMessage = ({ request }: { request: AmendmentRequest }) => {
  if (request.status === "REJECTED") {
    return (
      <div className="title-change-reason-form__rejection">
        <div className="title-change-reason-form__message-header">
          <div className="title-change-reason-form__message-title">
            <CloseCircleFilled />
            <Text strong>Запись отклонена</Text>
          </div>
          <Text>{formatDate(request.updatedAt)}</Text>
        </div>
        <div className="title-change-reason-form__rejection-content">
          <Text className="title-change-reason-form__message-caption">
            Комментарий от{" "}
            <UserBubbleLinkText>
              {request.rejectionAuthor || "—"}
            </UserBubbleLinkText>
          </Text>
          <div className="title-change-reason-form__rejection-comment">
            {request.rejectionComment || "Причина отклонения не указана"}
          </div>
        </div>
      </div>
    );
  }

  if (request.signedAt) {
    return (
      <div className="title-change-reason-form__signed">
        <div className="title-change-reason-form__message-title">
          <CheckCircleFilled />
          <Text>Подписано</Text>
        </div>
        <Text>{formatDate(request.signedAt)}</Text>
      </div>
    );
  }

  return null;
};

export const TitleChangeReasonForm = ({
  isReadOnly,
  request,
}: TitleChangeReasonFormProps) => {
  const [isDocModalVisible, setIsDocModalVisible] = useState(false);
  const updateRequest = useAmendmentsStore((state) => state.updateRequest);
  const { representative: buildingContractor } =
    useGetRepresentative("buildingContractor");

  const status = request?.status;
  const isDisabled = isReadOnly || status !== "NEW";
  const isSystemRequest = request?.requestSource === "SYSTEM";
  const responsibleOptions = useMemo(() => {
    const options = (buildingContractor?.representatives ?? []).map(
      (representative) => {
        const { user } = representative;
        const name = [user?.surname, user?.firstName, user?.middleName]
          .filter(Boolean)
          .join(" ");
        const snapshot: AmendmentResponsibleSnapshot = {
          id: representative.representativeId ?? user?.userId ?? name,
          name,
          organization: buildingContractor?.company?.fullName ?? "—",
          position: "Уполномоченный представитель",
          document:
            formatAdministrativeDocument(
              representative.administrativeDocument,
            ) || "—",
          documentUrl: representative.administrativeDocument?.documentUrl,
          email: user?.userEmail ?? "—",
          registryNumber:
            user?.specialistNumberBuilding ??
            user?.specialistNumberEngineering ??
            "—",
          nostroyNumber: user?.specialistNumberBuilding ?? "—",
          phone: user?.mobilePhone ?? "—",
        };

        return snapshot;
      },
    );

    return options.length > 0 ? options : FALLBACK_RESPONSIBLE_OPTIONS;
  }, [buildingContractor]);

  const update = (patch: Parameters<typeof updateRequest>[1]) => {
    if (request) {
      updateRequest(request.key, patch);
    }
  };

  const handleDocumentConfirm = (documents: DocumentItem[]) => {
    update({ document: documents[0]?.title ?? null });
    setIsDocModalVisible(false);
  };

  return (
    <div className="title-change-reason-form" id="title-change-reason">
      <Title level={4} className="title-change-reason-form__heading">
        Причина и основание изменений
      </Title>

      <Form layout="vertical" className="title-change-reason-form__form">
        <Form.Item label="Причина внесения изменений">
          <Input
            placeholder="Добавление данных, например"
            value={request?.reason ?? ""}
            onChange={(event) => update({ reason: event.target.value })}
            disabled={isDisabled}
          />
        </Form.Item>

        <Form.Item label="Основание для внесения изменений">
          <Input
            placeholder="Непреодолимое желание перемен, допустим"
            value={request?.basis ?? ""}
            onChange={(event) => update({ basis: event.target.value })}
            disabled={isDisabled}
          />
        </Form.Item>

        <div className="title-change-reason-form__doc-block">
          <div className="title-change-reason-form__doc-title">
            Документ из документария{" "}
            <span className="title-change-reason-form__optional">
              (необязательно)
            </span>
          </div>

          {!isDisabled ? (
            <>
              <Radio.Group
                className="title-change-reason-form__document-types"
                value={request?.documentType ?? "PERMIT"}
                onChange={(event) =>
                  update({ documentType: event.target.value })
                }
              >
                <Radio value="PERMIT">Разрешительная документация</Radio>
                <Radio value="EXECUTIVE">Исполнительная документация</Radio>
                <Radio value="SUPPORTING">Сопроводительная документация</Radio>
              </Radio.Group>
              {request?.document ? (
                <FileLink
                  name={request.document}
                  onDelete={() => update({ document: null })}
                />
              ) : (
                <Button
                  className="title-change-reason-form__add-button"
                  type="text"
                  icon={<PlusOutlined />}
                  onClick={() => setIsDocModalVisible(true)}
                >
                  Добавить
                </Button>
              )}
            </>
          ) : (
            <FileLink name={request?.document || "—"} />
          )}
        </div>

        <div className="title-change-reason-form__doc-block">
          <div className="title-change-reason-form__doc-title">
            Дополнительные файлы{" "}
            <span className="title-change-reason-form__optional">
              (необязательно)
            </span>
          </div>

          {!isDisabled && !request?.additionalFile ? (
            <Upload
              accept="application/pdf,.pdf"
              maxCount={1}
              showUploadList={false}
              beforeUpload={(file) => {
                const error = validateAmendmentFile(file);
                if (error) {
                  notification.error({ message: error });
                  return Upload.LIST_IGNORE;
                }
                update({
                  additionalFile: file.name,
                  additionalFileSize: file.size,
                });
                return false;
              }}
            >
              <Button
                className="title-change-reason-form__add-button"
                type="text"
                icon={<PlusOutlined />}
              >
                Добавить
              </Button>
            </Upload>
          ) : null}
          {request?.additionalFile ? (
            <FileLink
              name={request.additionalFile}
              onDelete={
                !isDisabled
                  ? () =>
                      update({
                        additionalFile: null,
                        additionalFileSize: null,
                      })
                  : undefined
              }
            />
          ) : null}
          {isDisabled && !request?.additionalFile ? (
            <FileLink name="—" />
          ) : null}
        </div>

        {isSystemRequest && !isDisabled ? (
          <Form.Item
            className="title-change-reason-form__date"
            label="Юридическая дата изменения"
          >
            <DatePicker
              value={
                request?.legalChangeDate ? dayjs(request.legalChangeDate) : null
              }
              format="DD.MM.YYYY"
              placeholder="Выберите дату"
              onChange={(date) =>
                update({
                  legalChangeDate: date
                    ? date.startOf("day").toISOString()
                    : null,
                })
              }
            />
          </Form.Item>
        ) : null}

        {!isDisabled ? (
          <Form.Item
            className="title-change-reason-form__responsible-select"
            label="Представитель, ответственный за внесение изменений и ведение записей о них"
          >
            <AmendmentResponsiblePicker
              options={responsibleOptions}
              value={request?.responsible || undefined}
              onChange={(responsible) =>
                update({
                  responsible: responsible.name,
                  responsiblePosition: responsible.position,
                  responsibleSnapshot: responsible,
                })
              }
            />
          </Form.Item>
        ) : (
          <>
            <div className="title-change-reason-form__legal-date">
              <Text>Юридическая дата изменения</Text>
              <Text>
                {formatDate(
                  request?.legalChangeDate ?? request?.updatedAt ?? null,
                )}
              </Text>
            </div>
            {request ? (
              <div className="title-change-reason-form__responsible-section">
                <Text>
                  Представитель, ответственный за внесение изменений и ведение
                  записей о них
                </Text>
                <ResponsibleCard request={request} />
              </div>
            ) : null}
            {request ? <RequestStatusMessage request={request} /> : null}
          </>
        )}
      </Form>

      <SectionDocumentModal
        visible={isDocModalVisible}
        onCancel={() => setIsDocModalVisible(false)}
        docType={DOCUMENT_TYPE_TO_MODAL_TYPE[request?.documentType ?? "PERMIT"]}
        onConfirm={handleDocumentConfirm}
      />
    </div>
  );
};
